import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, UserPlus, Mail, Phone, Calendar, Ticket, 
  DollarSign, TrendingUp, Search, Download, Filter,
  ChevronLeft, ChevronRight, RefreshCw, FileText, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import ProducerLayout from '../../components/producer/ProducerLayout';
import NotificationBell from '../../components/NotificationBell';

const ProducerCommunityPage = () => {
  const [buyers, setBuyers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [buyersRes, statsRes] = await Promise.all([
        api.get(`/producers/me/community?skip=${page * limit}&limit=${limit}`).catch(() => ({ data: [] })),
        api.get('/producers/me/dashboard').catch(() => ({ data: { stats: {} } }))
      ]);
      const buyersData = buyersRes.data || [];
      setBuyers(buyersData);
      setHasMore(buyersData.length === limit);
      setStats(statsRes.data?.stats || {});
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const exportCommunity = async () => {
    setExporting(true);
    try {
      const response = await api.get('/producers/me/community/export', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comunidad_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Lista exportada correctamente');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar la lista');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredBuyers = buyers.filter(buyer => 
    buyer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    buyer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    buyer.phone?.includes(searchTerm)
  );

  return (
    <ProducerLayout currentPage="community">
      <div className="p-4 lg:p-6" data-testid="producer-community-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-black text-white">Mi Comunidad</h1>
            <p className="text-zinc-400">Conocé a tus compradores y sus estadísticas</p>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell 
              userType="producer" 
              apiEndpoint="/producer/notifications" 
              title="Notificaciones" 
            />
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-400 hover:text-white"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button
              className="bg-gradient-to-r from-violet-600 to-pink-600 text-white"
              onClick={exportCommunity}
              disabled={exporting || buyers.length === 0}
              data-testid="export-community-btn"
            >
              {exporting ? (
                <RefreshCw size={16} className="mr-2 animate-spin" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Exportar CSV
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Users className="text-violet-500" size={18} />
              <span className="text-zinc-500 text-xs uppercase tracking-wide">Total</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.community_size || 0}</p>
            <p className="text-xs text-zinc-500">compradores únicos</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="text-green-500" size={18} />
              <span className="text-zinc-500 text-xs uppercase tracking-wide">Nuevos</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{stats?.new_buyers_month || 0}</p>
            <p className="text-xs text-zinc-500">este mes</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Ticket className="text-purple-500" size={18} />
              <span className="text-zinc-500 text-xs uppercase tracking-wide">Promedio</span>
            </div>
            <p className="text-2xl font-bold text-purple-400">
              {typeof stats?.avg_tickets_per_buyer === 'number' 
                ? stats.avg_tickets_per_buyer.toFixed(1) 
                : stats?.avg_tickets_per_buyer || '0'}
            </p>
            <p className="text-xs text-zinc-500">tickets/persona</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="text-blue-500" size={18} />
              <span className="text-zinc-500 text-xs uppercase tracking-wide">Recurrentes</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{stats?.recurring_buyers || 0}%</p>
            <p className="text-xs text-zinc-500">compraron +1 vez</p>
          </motion.div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <Input
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-800 text-white"
              data-testid="search-community"
            />
          </div>
        </div>

        {/* Buyers List */}
        {loading && buyers.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredBuyers.length === 0 && searchTerm ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded-xl"
          >
            <Search size={48} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Sin resultados</h3>
            <p className="text-zinc-500">No se encontraron compradores con "{searchTerm}"</p>
          </motion.div>
        ) : buyers.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded-xl"
          >
            <Users size={64} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Tu comunidad está vacía</h3>
            <p className="text-zinc-500 mb-4">Cuando vendas tickets, tus compradores aparecerán aquí</p>
            <div className="flex flex-col items-center gap-2 text-sm text-zinc-600">
              <p className="flex items-center gap-2">
                <FileText size={16} />
                Podrás ver nombre, email y teléfono
              </p>
              <p className="flex items-center gap-2">
                <DollarSign size={16} />
                Historial de compras y total gastado
              </p>
              <p className="flex items-center gap-2">
                <Download size={16} />
                Exportar lista completa a CSV
              </p>
            </div>
          </motion.div>
        ) : (
          <>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800 bg-zinc-900/50">
                      <th className="text-left p-4 text-zinc-400 font-medium text-xs uppercase tracking-wide">Comprador</th>
                      <th className="text-left p-4 text-zinc-400 font-medium text-xs uppercase tracking-wide hidden md:table-cell">Contacto</th>
                      <th className="text-center p-4 text-zinc-400 font-medium text-xs uppercase tracking-wide">Compras</th>
                      <th className="text-left p-4 text-zinc-400 font-medium text-xs uppercase tracking-wide hidden lg:table-cell">Última compra</th>
                      <th className="text-right p-4 text-zinc-400 font-medium text-xs uppercase tracking-wide">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBuyers.map((buyer, index) => (
                      <motion.tr
                        key={buyer.id || buyer.email || index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-violet-500/30 to-violet-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-violet-500 font-bold text-sm">
                                {buyer.name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-white truncate">{buyer.name || 'Sin nombre'}</p>
                              <p className="text-xs text-zinc-500 truncate md:hidden">{buyer.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <div className="space-y-1">
                            <p className="text-zinc-300 text-sm flex items-center gap-2">
                              <Mail size={12} className="text-zinc-600" />
                              {buyer.email}
                            </p>
                            {buyer.phone && (
                              <p className="text-zinc-500 text-xs flex items-center gap-2">
                                <Phone size={12} className="text-zinc-600" />
                                {buyer.phone}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium">
                            <Ticket size={12} />
                            {buyer.total_purchases || 0}
                          </span>
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <span className="text-zinc-400 text-sm flex items-center gap-2">
                            <Calendar size={12} className="text-zinc-600" />
                            {formatDate(buyer.last_purchase)}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-green-400 font-semibold">
                            {formatCurrency(buyer.total_spent)}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-zinc-500 text-sm">
                Mostrando {filteredBuyers.length} de {stats?.community_size || buyers.length} compradores
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  className="border-zinc-700"
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="flex items-center px-3 text-zinc-400 text-sm">
                  Página {page + 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore || loading}
                  className="border-zinc-700"
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </ProducerLayout>
  );
};

export default ProducerCommunityPage;
